import { createRouter, createWebHistory } from 'vue-router'
import { authService } from '@/services/authService'
import { setUnauthorizedHandler } from '@/services/apiClient'
import { sessionLockService } from '@/services/sessionLockService'
import { tokenRefreshService } from '@/services/tokenRefreshService'
import { toastService } from '@/services/toastService'

const LOCK_SCREEN_NAME = 'auth.lock-screen'

// Design System Routes
const designSystemChildRoutes = (prefix) => [
  {
    path: '',
    name: prefix + '.main',
    meta: { auth: true, name: 'Design System' },
    component: () => import('@/views/design-system/IndexPage.vue')
  }
]
// Auth Default Routes
const authChildRoutes = (prefix) => [
  {
    path: 'login',
    name: prefix + '.login',
    meta: { auth: false, name: 'Login' },
    component: () => import('@/views/auth/default/SignIn.vue')
  },
  {
    path: 'register',
    name: prefix + '.register',
    meta: { auth: false, name: 'Register' },
    component: () => import('@/views/auth/default/SignUp.vue')
  },
  {
    path: 'reset-password',
    name: prefix + '.reset-password',
    meta: { auth: false, name: 'Reset Password' },
    component: () => import('@/views/auth/default/ResetPassword.vue')
  },
  {
    path: 'two-factor',
    name: prefix + '.two-factor',
    meta: { auth: false, name: 'Two Factor' },
    component: () => import('@/views/auth/default/TwoFactorVerify.vue')
  },
  {
    path: 'varify-email',
    name: prefix + '.varify-email',
    meta: { auth: false, name: 'Varify Email' },
    component: () => import('@/views/auth/default/VarifyEmail.vue')
  },
  {
    path: 'lock-screen',
    name: prefix + '.lock-screen',
    meta: { auth: false, lockScreen: true, name: 'Lock Screen' },
    component: () => import('@/views/auth/default/LockScreen.vue')
  }
]

// Dashboard routes
const dashboardRoutes = (prefix) => [
  {
    path: '',
    name: prefix + '.dashboard',
    meta: { auth: true, name: 'Home', isBanner: false },
    component: () => import('@/views/dashboards/IndexPage.vue')
  }
]
// Default routes
const defaultChildRoutes = (prefix) => [
  {
    path: '',
    name: prefix + '.dashboard',
    meta: { auth: true, name: 'Home', isBanner: true },
    component: () => import('@/views/dashboards/IndexPage.vue')
  },
  {
    path: '/cortex',
    name: prefix + '.cortex',
    meta: { auth: true, name: 'Cortex', isBanner: false },
    component: () => import('@/views/cortex/CortexPage.vue')
  },
  // Spacial Pages
  {
    path: '/billing',
    name: prefix + '.billing',
    meta: { auth: true, name: 'Billing', isBanner: true },
    component: () => import('@/views/spacial-pages/BillingPage.vue')
  },
  {
    path: '/calender',
    name: prefix + '.calender',
    meta: { auth: true, name: 'Calender', isBanner: true },
    component: () => import('@/views/spacial-pages/CalenderPage.vue')
  },
  {
    path: '/kanban',
    name: prefix + '.kanban',
    meta: { auth: true, name: 'Kanban', isBanner: true },
    component: () => import('@/views/spacial-pages/KanbanPage.vue')
  },
  {
    path: '/pricing',
    name: prefix + '.pricing',
    meta: { auth: true, name: 'Pricing', isBanner: true },
    component: () => import('@/views/spacial-pages/PricingPage.vue')
  },
  {
    path: '/timeline',
    name: prefix + '.timeline',
    meta: { auth: true, name: 'Timeline', isBanner: true },
    component: () => import('@/views/spacial-pages/TimelinePage.vue')
  },
  {
    path: '/rtl-support',
    name: prefix + '.rtlsupport',
    meta: { auth: true, name: 'RTL-Support', isBanner: true },
    component: () => import('@/views/spacial-pages/RtlSupport.vue')
  },
  // Users Pages
  {
    path: '/user-list',
    name: prefix + '.user-list',
    meta: { auth: true, name: 'User List', isBanner: true },
    component: () => import('@/views/user/ListPage.vue')
  },
  {
    path: '/user-add',
    name: prefix + '.user-add',
    meta: { auth: true, name: 'User Add', isBanner: true },
    component: () => import('@/views/user/AddPage.vue')
  },
  {
    path: '/user-profile',
    name: prefix + '.user-profile',
    meta: { auth: true, name: 'Profil', isBanner: false },
    component: () => import('@/views/user/ProfilePage.vue')
  },
  {
    path: '/privacy-setting',
    name: prefix + '.user-privacy-setting',
    meta: { auth: true, name: 'Modifier mot de passe', isBanner: false },
    component: () => import('@/views/user/PrivacySetting.vue')
  },
  // Widgets Pages
  {
    path: '/widget-basic',
    name: prefix + '.widget-basic',
    meta: { auth: true, name: 'Widget Basic', isBanner: true },
    component: () => import('@/views/widgets/WidgetBasic.vue')
  },
  {
    path: '/widget-chart',
    name: prefix + '.widget-chart',
    meta: { auth: true, name: 'Widget Chart', isBanner: true },
    component: () => import('@/views/widgets/WidgetChart.vue')
  },
  {
    path: '/widget-card',
    name: prefix + '.widget-card',
    meta: { auth: true, name: 'Widget Card', isBanner: true },
    component: () => import('@/views/widgets/WidgetCard.vue')
  },
  // Map Pages
  {
    path: '/map-google',
    name: prefix + '.map-google',
    meta: { auth: true, name: 'Google Map', isBanner: true },
    component: () => import('@/views/maps/GooglePage.vue')
  },
  {
    path: '/map-vector',
    name: prefix + '.map-vector',
    meta: { auth: true, name: 'Vector Map', isBanner: true },
    component: () => import('@/views/maps/VectorPage.vue')
  },
  // Form Pages
  {
    path: '/elements',
    name: prefix + '.elements',
    meta: { auth: true, name: 'Elements', isBanner: true },
    component: () => import('@/views/forms/ElementsPage.vue')
  },
  {
    path: '/validation',
    name: prefix + '.validation',
    meta: { auth: true, name: 'Validation', isBanner: true },
    component: () => import('@/views/forms/ValidationPage.vue')
  },
  {
    path: '/wizard',
    name: prefix + '.wizard',
    meta: { auth: true, name: 'Wizard', isBanner: true },
    component: () => import('@/views/forms/WizardPage.vue')
  },
  // Table Pages
  {
    path: '/bootstrap-table',
    name: prefix + '.bootstrap-table',
    meta: { auth: true, name: 'Botstrap Table', isBanner: true },
    component: () => import('@/views/tables/BootstrapTable.vue')
  },
  {
    path: '/datatable',
    name: prefix + '.data-table',
    meta: { auth: true, name: 'Data Table', isBanner: true },
    component: () => import('@/views/tables/DataTable.vue')
  },
  // Icons Pages
  {
    path: '/icons/solid',
    name: prefix + '.icons.solid',
    meta: { auth: true, name: 'Solid Icon', isBanner: true },
    component: () => import('@/views/icons/SolidIcon.vue')
  },
  {
    path: '/icons/outlined',
    name: prefix + '.icons.outlined',
    meta: { auth: true, name: 'Outlined Icon', isBanner: true },
    component: () => import('@/views/icons/OutlinedIcon.vue')
  },
  {
    path: '/icons/dual-tone',
    name: prefix + '.icons.dual-tone',
    meta: { auth: true, name: 'Dual Tone Icon', isBanner: true },
    component: () => import('@/views/icons/DualToneIcon.vue')
  },
  // Extra Pages
  {
    path: '/privacy-policy',
    name: prefix + '.privacy-policy',
    meta: { auth: true, name: 'Privacy Policy', isBanner: true },
    component: () => import('@/views/extra/PrivacyPolicy.vue')
  },
  {
    path: '/terms-and-conditions',
    name: prefix + '.terms-and-conditions',
    meta: { auth: true, name: 'Terms and Conditions', isBanner: true },
    component: () => import('@/views/extra/TermsAndConditions.vue')
  },
  {
    path: '/admin-permissions',
    name: prefix + '.admin-permissions',
    meta: { auth: true, name: 'Admin Permissions', isBanner: true },
    component: () => import('@/views/admin/AdminPage.vue')
  }
]

const errorRoutes = (prefix) => [
  // Error Pages
  {
    path: '404',
    name: prefix + '.404',
    meta: { auth: true, name: 'Error 404', isBanner: true },
    component: () => import('@/views/errors/Error404Page.vue')
  },
  {
    path: '500',
    name: prefix + '.500',
    meta: { auth: true, name: 'Error 500', isBanner: true },
    component: () => import('@/views/errors/Error500Page.vue')
  },
  {
    path: 'maintenance',
    name: prefix + '.maintenance',
    meta: { auth: true, name: 'Maintenance', isBanner: true },
    component: () => import('@/views/errors/MaintenancePage.vue')
  }
]

// BatiPilot Routes - Projet
const projetChildRoutes = (prefix) => [
  {
    path: 'client-create',
    name: prefix + '.client-create',
    meta: { auth: true, name: 'Créer Client', isBanner: false },
    component: () => import('@/views/batipilot/projet/ClientCreatePage.vue')
  },
  {
    path: 'client-list',
    name: prefix + '.client-list',
    meta: { auth: true, name: 'Liste Clients', isBanner: false },
    component: () => import('@/views/batipilot/projet/ClientListPage.vue')
  },
  {
    path: 'project-create',
    name: prefix + '.project-create',
    meta: { auth: true, name: 'Créer Projet', isBanner: false },
    component: () => import('@/views/batipilot/projet/ProjectCreatePage.vue')
  },
  {
    path: 'project-list',
    name: prefix + '.project-list',
    meta: { auth: true, name: 'Liste Projets', isBanner: false },
    component: () => import('@/views/batipilot/projet/ProjectListPage.vue')
  },
  {
    path: 'project-task',
    name: prefix + '.project-task',
    meta: { auth: true, name: 'Tâches Projet', isBanner: false },
    component: () => import('@/views/batipilot/projet/ProjectTaskPage.vue')
  },
  {
    path: 'project-resource',
    name: prefix + '.project-resource',
    meta: { auth: true, name: 'Allocation Ressources', isBanner: false },
    component: () => import('@/views/batipilot/projet/ProjectResourcePage.vue')
  }
]

// BatiPilot Routes - Prestataire
const prestataireChildRoutes = (prefix) => [
  {
    path: 'provider-create',
    name: prefix + '.provider-create',
    meta: { auth: true, name: 'Créer Prestataire', isBanner: false },
    component: () => import('@/views/batipilot/prestataire/ProviderCreatePage.vue')
  },
  {
    path: 'provider-pay',
    name: prefix + '.provider-pay',
    meta: { auth: true, name: 'Payer Prestataire', isBanner: false },
    component: () => import('@/views/batipilot/prestataire/ProviderPayPage.vue')
  },
  {
    path: 'payment-history',
    name: prefix + '.payment-history',
    meta: { auth: true, name: 'Historique Paiement', isBanner: false },
    component: () => import('@/views/batipilot/prestataire/ProviderPaymentHistoryPage.vue')
  }
]

// BatiPilot Routes - Commercial
const commercialChildRoutes = (prefix) => [
  {
    path: 'quote-create',
    name: prefix + '.quote-create',
    meta: { auth: true, name: 'Créer Devis', isBanner: false },
    component: () => import('@/views/batipilot/commercial/QuoteCreatePage.vue')
  },
  {
    path: 'quote-edit/:id',
    name: prefix + '.quote-edit',
    meta: { auth: true, name: 'Modifier Devis', isBanner: false },
    component: () => import('@/views/batipilot/commercial/QuoteCreatePage.vue')
  },
  {
    path: 'quote-history',
    name: prefix + '.quote-history',
    meta: { auth: true, name: 'Historique Devis', isBanner: false },
    component: () => import('@/views/batipilot/commercial/QuoteHistoryPage.vue')
  },
  {
    path: 'invoice-create',
    name: prefix + '.invoice-create',
    meta: { auth: true, name: 'Créer Facture', isBanner: false },
    component: () => import('@/views/batipilot/commercial/InvoiceCreatePage.vue')
  },
  {
    path: 'invoice-history',
    name: prefix + '.invoice-history',
    meta: { auth: true, name: 'Historique Factures', isBanner: false },
    component: () => import('@/views/batipilot/commercial/InvoiceHistoryPage.vue')
  },
  {
    path: 'payment-tracking',
    name: prefix + '.payment-tracking',
    meta: { auth: true, name: 'Suivi Paiements', isBanner: false },
    component: () => import('@/views/batipilot/commercial/PaymentTrackingPage.vue')
  }
]

// BatiPilot Routes - Finance
const financeChildRoutes = (prefix) => [
  {
    path: 'entrees',
    name: prefix + '.entrees',
    meta: { auth: true, name: "Entrées d'Argent", isBanner: false },
    component: () => import('@/views/batipilot/finance/EntreesPage.vue')
  },
  {
    path: 'charge-fixe',
    name: prefix + '.charge-fixe',
    meta: { auth: true, name: 'Charges Fixes', isBanner: false },
    component: () => import('@/views/batipilot/finance/ChargeFixePage.vue')
  },
  {
    path: 'charge-variable',
    name: prefix + '.charge-variable',
    meta: { auth: true, name: 'Charges Variables', isBanner: false },
    component: () => import('@/views/batipilot/finance/ChargeVariablePage.vue')
  },
  {
    path: 'divers-expense',
    name: prefix + '.divers-expense',
    meta: { auth: true, name: 'Dépenses Divers', isBanner: false },
    component: () => import('@/views/batipilot/finance/DiversExpensePage.vue')
  },
  {
    path: 'supplier-invoice',
    name: prefix + '.supplier-invoice',
    meta: { auth: true, name: 'Factures Reçues', isBanner: false },
    component: () => import('@/views/batipilot/finance/SupplierInvoicePage.vue')
  },
  {
    path: 'treasury',
    name: prefix + '.treasury',
    meta: { auth: true, name: 'Trésorerie', isBanner: false },
    component: () => import('@/views/batipilot/finance/TreasuryPage.vue')
  }
]

// BatiPilot Routes - RH
const rhChildRoutes = (prefix) => [
  {
    path: 'employee-list',
    name: prefix + '.employee-list',
    meta: { auth: true, name: 'Employés', isBanner: false },
    component: () => import('@/views/batipilot/rh/EmployeeListPage.vue')
  },
  {
    path: 'payroll',
    name: prefix + '.payroll',
    meta: { auth: true, name: 'Paie & Salaires', isBanner: false },
    component: () => import('@/views/batipilot/rh/PayrollPage.vue')
  }
]

// BatiPilot Routes - Rapports
const rapportsChildRoutes = (prefix) => [
  {
    path: 'report-client',
    name: prefix + '.report-client',
    meta: { auth: true, name: 'Rapport Client', isBanner: false },
    component: () => import('@/views/batipilot/rapports/ReportClientPage.vue')
  },
  {
    path: 'report-project',
    name: prefix + '.report-project',
    meta: { auth: true, name: 'Rapport Projet', isBanner: false },
    component: () => import('@/views/batipilot/rapports/ReportProjectPage.vue')
  },
  {
    path: 'report-quote',
    name: prefix + '.report-quote',
    meta: { auth: true, name: 'Rapport Devis', isBanner: false },
    component: () => import('@/views/batipilot/rapports/ReportQuotePage.vue')
  },
  {
    path: 'report-finance',
    name: prefix + '.report-finance',
    meta: { auth: true, name: 'Rapport Finance', isBanner: false },
    component: () => import('@/views/batipilot/rapports/ReportFinancePage.vue')
  },
  {
    path: 'report-detail',
    name: prefix + '.report-detail',
    meta: { auth: true, name: 'Rapport Détaillé', isBanner: false },
    component: () => import('@/views/batipilot/rapports/ReportDetailPage.vue')
  }
]

// BatiPilot Routes - Paramètres
const parametresChildRoutes = (prefix) => [
  {
    path: 'user-setting',
    name: prefix + '.user-setting',
    meta: { auth: true, name: 'Utilisateur', isBanner: false },
    component: () => import('@/views/batipilot/parametres/UserSettingPage.vue')
  },
  {
    path: 'role-setting',
    name: prefix + '.role-setting',
    meta: { auth: true, name: 'Rôle', isBanner: false },
    component: () => import('@/views/batipilot/parametres/RoleSettingPage.vue')
  },
  {
    path: 'general-setting',
    name: prefix + '.general-setting',
    meta: { auth: true, name: 'Paramètres Généraux', isBanner: false },
    component: () => import('@/views/batipilot/parametres/GeneralSettingPage.vue')
  },
  {
    path: 'email-setting',
    name: prefix + '.email-setting',
    meta: { auth: true, name: 'Paramètres Email', isBanner: false },
    component: () => import('@/views/batipilot/parametres/EmailSettingPage.vue')
  },
  {
    path: 'email-template-setting',
    name: prefix + '.email-template-setting',
    meta: { auth: true, name: 'Modèles de Mail', isBanner: false },
    component: () => import('@/views/batipilot/parametres/EmailTemplateSettingPage.vue')
  },
  {
    path: 'company-setting',
    name: prefix + '.company-setting',
    meta: { auth: true, name: 'Entreprise', isBanner: false },
    component: () => import('@/views/batipilot/parametres/CompanySettingPage.vue')
  },
  {
    path: 'charge-fixe-type-setting',
    name: prefix + '.charge-fixe-type-setting',
    meta: { auth: true, name: 'Type Charge Fixe', isBanner: false },
    component: () => import('@/views/batipilot/parametres/ChargeFixeTypeSettingPage.vue')
  }
]

const routes = [
  {
    path: '/',
    redirect: '/dashboard'
  },
  {
    path: '/design-system',
    name: 'design-system',
    component: () => import('../layouts/guest/BlankLayout.vue'),
    children: designSystemChildRoutes('design-system')
  },
  // Default Pages
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('../layouts/DefaultLayout.vue'),
    children: defaultChildRoutes('default')
  },

  // ===== BatiPilot Routes =====
  // Projet
  {
    path: '/projet',
    name: 'projet',
    component: () => import('../layouts/DefaultLayout.vue'),
    children: projetChildRoutes('projet')
  },

  // Prestataire
  {
    path: '/prestataire',
    name: 'prestataire',
    component: () => import('../layouts/DefaultLayout.vue'),
    children: prestataireChildRoutes('prestataire')
  },

  // Commercial
  {
    path: '/commercial',
    name: 'commercial',
    component: () => import('../layouts/DefaultLayout.vue'),
    children: commercialChildRoutes('commercial')
  },

  // Finance
  {
    path: '/finance',
    name: 'finance',
    component: () => import('../layouts/DefaultLayout.vue'),
    children: financeChildRoutes('finance')
  },

  // RH
  {
    path: '/rh',
    name: 'rh',
    component: () => import('../layouts/DefaultLayout.vue'),
    children: rhChildRoutes('rh')
  },

  // Rapports
  {
    path: '/rapports',
    name: 'rapports',
    component: () => import('../layouts/DefaultLayout.vue'),
    redirect: { name: 'rapports.report-client' },
    children: rapportsChildRoutes('rapports')
  },

  // Paramètres
  {
    path: '/parametres',
    name: 'parametres',
    component: () => import('../layouts/DefaultLayout.vue'),
    children: parametresChildRoutes('parametres')
  },
  // ===== End BatiPilot Routes =====

  // Menu Styles
  {
    path: '/horizontal',
    name: 'horizontal',
    component: () => import('../layouts/menu-styles/HorizontalLayout.vue'),
    children: dashboardRoutes('horizontal')
  },
  {
    path: '/dual-horizontal',
    name: 'dual-horizontal',
    component: () => import('../layouts/menu-styles/DualHorizontalLayout.vue'),
    children: dashboardRoutes('dual-horizontal')
  },
  {
    path: '/dual-compact',
    name: 'dual-compact',
    component: () => import('../layouts/menu-styles/DualCompactLayout.vue'),
    children: dashboardRoutes('dual-compact')
  },
  {
    path: '/boxed',
    name: 'boxed',
    component: () => import('../layouts/menu-styles/BoxedLayout.vue'),
    children: dashboardRoutes('boxed')
  },
  {
    path: '/boxed-fancy',
    name: 'boxed-fancy',
    component: () => import('../layouts/menu-styles/BoxedFancyLayout.vue'),
    children: dashboardRoutes('boxed-fancy')
  },

  // Auth Skins
  {
    path: '/auth',
    name: 'auth',
    component: () => import('../layouts/guest/BlankLayout.vue'),
    children: authChildRoutes('auth')
  },
  // Errors Pages
  {
    path: '/errors',
    name: 'errors',
    component: () => import('../layouts/guest/BlankLayout.vue'),
    children: errorRoutes('errors')
  }
]

const router = createRouter({
  linkActiveClass: 'active',
  linkExactActiveClass: 'exact-active',
  history: createWebHistory(process.env.BASE_URL),
  base: process.env.BASE_URL,
  routes
})


router.beforeEach((to) => {
  const isAuthenticated = authService.isAuthenticated()
  const isLockScreen = to.name === LOCK_SCREEN_NAME
  const isPublic = to.matched.some((record) => record.meta.auth === false) && !isLockScreen

  if (isLockScreen) {
    if (!isAuthenticated) {
      return { name: 'auth.login', query: { redirect: to.query.redirect || '/dashboard' } }
    }
    if (!sessionLockService.isLocked()) {
      const redirect = typeof to.query.redirect === 'string' ? to.query.redirect : sessionLockService.getLockRedirect()
      return redirect.startsWith('/') ? redirect : { name: 'default.dashboard' }
    }
    return true
  }

  if (isAuthenticated && sessionLockService.isLocked()) {
    return { name: LOCK_SCREEN_NAME, query: { redirect: to.fullPath } }
  }

  if (!isPublic && !isAuthenticated) {
    return { name: 'auth.login', query: { redirect: to.fullPath } }
  }

  if (isPublic && isAuthenticated && to.name === 'auth.login') {
    return { name: 'default.dashboard' }
  }

  if (to.name === 'auth.two-factor' && !authService.getTwoFactorPending()?.challengeToken) {
    return { name: 'auth.login' }
  }

  return true
})

const handleApiUnauthorized = async (message) => {
  if (authService.getRefreshToken()) {
    await tokenRefreshService.refreshIfNeeded()
    const path = router.currentRoute.value.fullPath
    sessionLockService.lock(path)
    toastService.warning(
      'Session verrouillée pour inactivité. Saisissez votre mot de passe pour continuer.',
      'Verrouillage automatique'
    )
    if (router.currentRoute.value.name !== LOCK_SCREEN_NAME) {
      router.push({ name: LOCK_SCREEN_NAME, query: { redirect: path } })
    }
    return
  }

  authService.logout()
  sessionLockService.clear()
  tokenRefreshService.stop()
  const text = message || 'Session expirée. Veuillez vous reconnecter.'
  toastService.error(text, 'Session terminée')
  if (router.currentRoute.value.name !== 'auth.login') {
    router.push({ name: 'auth.login', query: { redirect: router.currentRoute.value.fullPath } })
  }
}

setUnauthorizedHandler(handleApiUnauthorized)

export default router
